import { defaultSiteContent, type SiteContent, type SocialIconName } from "../siteData";
import { pb } from "./pocketbase";

type SiteSettingsRecord = {
  title?: string;
  author?: string;
  description?: string;
  siteUrl?: string;
  ctaLabel?: string;
  ctaHref?: string;
  spotifyTitle?: string;
  spotifySrc?: string;
  contactHeading?: string;
  footerCopyright?: string;
};

type SocialLinkRecord = {
  label?: string;
  href?: string;
  icon?: string;
};

type ContactEmailRecord = {
  email?: string;
};

const socialIconNames = new Set<SocialIconName>(["bandcamp", "beatport", "facebook", "instagram", "tiktok", "twitch", "youtube"]);
type CmsSocialLink = SiteContent["socialLinks"][number];

export async function loadCmsContent(): Promise<SiteContent> {
  try {
    const [settings, socialLinks, contactEmails] = await Promise.all([
      pb.collection("site_settings").getFirstListItem<SiteSettingsRecord>('key = "main"'),
      pb.collection("social_links").getFullList<SocialLinkRecord>({
        filter: "active = true",
        sort: "sort,label",
      }),
      pb.collection("contact_emails").getFullList<ContactEmailRecord>({
        filter: "active = true",
        sort: "sort,email",
      }),
    ]);

    return {
      meta: {
        ...defaultSiteContent.meta,
        title: settings.title || defaultSiteContent.meta.title,
        author: settings.author || defaultSiteContent.meta.author,
        description: settings.description || defaultSiteContent.meta.description,
        url: settings.siteUrl || defaultSiteContent.meta.url,
      },
      images: defaultSiteContent.images,
      cta: {
        label: settings.ctaLabel || defaultSiteContent.cta.label,
        href: settings.ctaHref || defaultSiteContent.cta.href,
      },
      socialLinks: socialLinks.map(normalizeSocialLink).filter(isCmsSocialLink),
      instagram: defaultSiteContent.instagram,
      spotify: {
        title: settings.spotifyTitle || defaultSiteContent.spotify.title,
        src: settings.spotifySrc || defaultSiteContent.spotify.src,
      },
      contact: {
        heading: settings.contactHeading || defaultSiteContent.contact.heading,
        emails: contactEmails.map(record => record.email).filter(isNonEmptyString),
      },
      footer: {
        copyright: settings.footerCopyright || defaultSiteContent.footer.copyright,
      },
    };
  } catch (error) {
    if (!isExpectedCmsUnavailable(error)) {
      console.warn("Unable to load PocketBase CMS content; using local defaults.", error);
    }

    return defaultSiteContent;
  }
}

function isExpectedCmsUnavailable(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }

  const status = "status" in error ? error.status : undefined;
  return status === 0 || status === 404;
}

function normalizeSocialLink(link: SocialLinkRecord) {
  return {
    label: link.label || "",
    href: link.href || "",
    icon: normalizeSocialIcon(link.icon),
  };
}

function isCmsSocialLink(link: ReturnType<typeof normalizeSocialLink>): link is CmsSocialLink {
  return Boolean(link.label && link.href && link.icon);
}

function normalizeSocialIcon(icon: string | undefined): SocialIconName | undefined {
  if (!icon) {
    return undefined;
  }

  return socialIconNames.has(icon as SocialIconName) ? (icon as SocialIconName) : undefined;
}

function isNonEmptyString(value: string | undefined): value is string {
  return Boolean(value);
}
